/**
 * Deterministic, client-side generator for the "Get More Customers"
 * panel that renders below the full-width Localytics Score breakdown in
 * the Business Review module.
 *
 * The panel always shows EXACTLY 3 acquisition plays, in a fixed order:
 *
 *   1. Reviews / map pack — get more reviews to rank higher in the Google
 *      map pack. Compares this business's review velocity against the #1
 *      competitor's velocity.
 *   2. Website traffic — grow organic traffic to outrank competitors.
 *      Compares this business's monthly traffic against the #1
 *      competitor's traffic.
 *   3. PPC ads — if the business is NOT running ads and competitors are,
 *      tell them to start. If they ARE running ads, tell them to study
 *      and copy their competitors' best-performing ads.
 *
 * This is deterministic (not Gemini-backed) for the same reasons the
 * to-do bullets were made deterministic: the panel must render on public
 * landing pages where the visitor is unauthenticated, and the content is
 * a straightforward function of the input data (velocity, traffic, ad
 * volume comparisons). Keeping it deterministic guarantees the exact
 * competitor comparisons the product requires.
 *
 * Output shape (consumed by GetMoreCustomersCard):
 *   [
 *     { key: 'reviews', category: 'Reviews', action: '...', body: '...' },
 *     { key: 'traffic', category: 'Website Traffic', action: '...', body: '...' },
 *     { key: 'ppc',     category: 'Paid Ads', action: '...', body: '...' },
 *   ]
 */

const toFiniteNumber = (val) => {
  if (val === null || val === undefined || val === '') return null;
  const n = typeof val === 'number' ? val : parseFloat(val);
  return Number.isFinite(n) ? n : null;
};

const formatCount = (val) => {
  const n = toFiniteNumber(val);
  if (n === null) return null;
  if (Math.abs(n) >= 1000) return Math.round(n).toLocaleString();
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(1);
};

const cleanName = (name) => {
  const n = (name || '').toString().trim();
  return n.length > 0 ? n : null;
};

/**
 * Reviews / map-pack play. Compares recent review velocity to the top
 * competitor so the owner can see exactly how much ground they need to
 * make up (or how far they lead) to win the map pack.
 */
const buildReviewsPlay = (reviews = {}) => {
  const self = toFiniteNumber(reviews.self);
  const competitorVal = toFiniteNumber(reviews.competitorValue);
  const competitorName = cleanName(reviews.competitorName);

  if (self !== null && competitorVal !== null && competitorName) {
    const selfStr = formatCount(self);
    const compStr = formatCount(competitorVal);
    if (self < competitorVal) {
      return {
        key: 'reviews',
        category: 'Reviews',
        action: 'Win the map pack with more reviews',
        body: `Your review velocity is ${selfStr} recent reviews vs. ${competitorName}'s ${compStr}. Review count and velocity are the biggest drivers of Google map-pack ranking — text every customer a one-tap Google review link within 24 hours of service to close the gap.`,
      };
    }
    return {
      key: 'reviews',
      category: 'Reviews',
      action: 'Extend your map-pack review lead',
      body: `Your review velocity of ${selfStr} recent reviews already beats ${competitorName}'s ${compStr} — the top signal for map-pack ranking. Keep the lead compounding: text every customer a one-tap Google review link within 24 hours of service.`,
    };
  }

  if (self !== null) {
    return {
      key: 'reviews',
      category: 'Reviews',
      action: 'Win the map pack with more reviews',
      body: `You've earned ${formatCount(self)} reviews recently. Review count and velocity are the biggest drivers of Google map-pack ranking — text every customer a one-tap Google review link within 24 hours of service to climb.`,
    };
  }

  return {
    key: 'reviews',
    category: 'Reviews',
    action: 'Win the map pack with more reviews',
    body: 'Review count and velocity are the biggest drivers of Google map-pack ranking. Text every customer a one-tap Google review link within 24 hours of service to out-review your competitors.',
  };
};

/**
 * Website-traffic play. Same structure as the reviews play, comparing
 * monthly organic traffic to the top competitor.
 */
const buildTrafficPlay = (traffic = {}) => {
  const self = toFiniteNumber(traffic.self);
  const competitorVal = toFiniteNumber(traffic.competitorValue);
  const competitorName = cleanName(traffic.competitorName);

  if (self !== null && competitorVal !== null && competitorName) {
    const selfStr = formatCount(self);
    const compStr = formatCount(competitorVal);
    if (self < competitorVal) {
      return {
        key: 'traffic',
        category: 'Website Traffic',
        action: 'Grow website traffic to outrank rivals',
        body: `Your site pulls ${selfStr} monthly visits vs. ${competitorName}'s ${compStr}. More traffic means more calls and booked jobs — publish service-area landing pages and earn local citations to close the gap.`,
      };
    }
    return {
      key: 'traffic',
      category: 'Website Traffic',
      action: 'Defend your website-traffic lead',
      body: `Your ${selfStr} monthly visits already beat ${competitorName}'s ${compStr}. Keep compounding it: ship a new service-area page each month and refresh your top-performing pages to stay ahead.`,
    };
  }

  if (self !== null) {
    return {
      key: 'traffic',
      category: 'Website Traffic',
      action: 'Grow website traffic to outrank rivals',
      body: `Your site pulls ${formatCount(self)} monthly visits. More traffic means more calls and booked jobs — publish service-area landing pages and earn local citations to climb past competitors.`,
    };
  }

  return {
    key: 'traffic',
    category: 'Website Traffic',
    action: 'Grow website traffic to outrank rivals',
    body: 'More organic traffic means more calls and booked jobs. Publish service-area landing pages and earn local citations to out-rank competitors in search.',
  };
};

/**
 * PPC / paid-ads play. Branches on whether the business runs ads and
 * whether competitors do:
 *   - not running + competitors are → start running ads
 *   - not running + no competitors  → get ahead of the market
 *   - running                        → study and copy competitor ads
 */
const buildPpcPlay = (ads = {}) => {
  const runningAds =
    ads.runningAds === true || (toFiniteNumber(ads.adCount) || 0) > 0;
  const adCount = toFiniteNumber(ads.adCount) || 0;
  const competitorsAdvertising = ads.competitorsAdvertising === true;
  const topAdvertiserName = cleanName(ads.topAdvertiserName);
  const topAdvertiserCount = toFiniteNumber(ads.topAdvertiserCount);
  const avgAdsMarket = toFiniteNumber(ads.avgAdsMarket);

  if (!runningAds) {
    if (competitorsAdvertising) {
      const rivalClause =
        topAdvertiserName && topAdvertiserCount
          ? `${topAdvertiserName} is running ${formatCount(topAdvertiserCount)} live ads`
          : 'your competitors are already running paid ads';
      const avgClause =
        avgAdsMarket && avgAdsMarket > 0
          ? ` (market average ${formatCount(avgAdsMarket)} ads)`
          : '';
      return {
        key: 'ppc',
        category: 'Paid Ads',
        action: 'Start running Google ads',
        body: `You're running 0 paid ads while ${rivalClause}${avgClause}. They're capturing the high-intent searches you're missing — launch Google Local Services Ads and a Search campaign on your core services to compete.`,
      };
    }
    return {
      key: 'ppc',
      category: 'Paid Ads',
      action: 'Get ahead with Google ads',
      body: "No competitor in your market is running paid ads yet. Launch Google Local Services Ads now to own the high-intent searches before a rival starts bidding on them.",
    };
  }

  if (topAdvertiserName && topAdvertiserCount) {
    return {
      key: 'ppc',
      category: 'Paid Ads',
      action: "Copy your competitors' best ads",
      body: `You're running ${formatCount(adCount)} paid ads — now sharpen them. Study ${topAdvertiserName}'s ${formatCount(topAdvertiserCount)} live ads and copy their best headlines, offers, and keywords into your own campaigns to win more clicks.`,
    };
  }

  return {
    key: 'ppc',
    category: 'Paid Ads',
    action: 'Sharpen your paid ads',
    body: `You're running ${formatCount(adCount)} paid ads and no competitor is advertising. Double down: test new headlines and offers to capture the entire paid market before rivals move in.`,
  };
};

/**
 * Build the 3 "Get More Customers" acquisition plays.
 *
 * @param {Object} params
 * @param {Object} [params.reviews] - { self, competitorName, competitorValue }
 * @param {Object} [params.traffic] - { self, competitorName, competitorValue }
 * @param {Object} [params.ads]     - {
 *     runningAds, adCount, competitorsAdvertising,
 *     topAdvertiserName, topAdvertiserCount, avgAdsMarket
 *   }
 * @returns {Array<{key:string, category:string, action:string, body:string}>}
 */
export function buildGetMoreCustomers({ reviews, traffic, ads } = {}) {
  return [
    buildReviewsPlay(reviews || {}),
    buildTrafficPlay(traffic || {}),
    buildPpcPlay(ads || {}),
  ];
}

const getMoreCustomersBuilder = { buildGetMoreCustomers };

export default getMoreCustomersBuilder;
